// This is the replacement for `src/rxdb/BorerOperatorStateFeed/handleOperatorStateFeedEvents.ts`.
import { RxError } from 'rxdb';

import { isJestOrStorybook } from '../../../test-helpers/isJestOrStorybook';
import { BorerOperatorChange } from '../../BorerOperatorChangeFeed/queryBuilder';
import { BorerOperatorState } from '../../BorerOperatorStateFeed/queryBuilder';
import { checkForExpiredToken } from '../../handleReplicationErrors';
import { crumbleRxDocumentData } from '../bakery/crumbleRxDocument';
import { Database } from '../Database';
import { logError } from '../logError';
import { crumbleReplicationState, ReplicationState } from '../replicate';

const recordIdsFailedToSyncCount: Record<string, number> = {};

const increaseFailedToSyncCount = (recordId: string) => {
  if (!recordIdsFailedToSyncCount[recordId]) {
    recordIdsFailedToSyncCount[recordId] = 1;
  }
  recordIdsFailedToSyncCount[recordId] += 1;
};

const recordIdHasFailedToSyncMoreThanXTimes = (recordId: string, xTimes = 10) => {
  return recordIdsFailedToSyncCount[recordId] >= xTimes;
};

export const handleOperatorStateFeedEvents = async (
  stateFeedSyncState: ReplicationState<BorerOperatorState>,
  borerOperatorChangeState: ReplicationState<BorerOperatorChange>,
  db: Database,
) => {
  // Successfully coded states, hide temp states from scheduler view
  stateFeedSyncState.received$.subscribe(async doc => {
    if (doc.isDeleted || doc._deleted) return;
    try {
      const borerStateId = doc.borerStateId;
      if (!borerStateId)
        throw new Error(
          'handleOperatorStateFeedEvents.ts:37 Doc missing borerStateId, cannot query for temp states',
        );

      const matchingTempDocs = await db.collections.borer_operator_change_feed
        ?.find({
          selector: {
            borerStateId,
            showInSchedulerView: true,
          },
        })
        .exec();
      if (!matchingTempDocs) return;

      for (const tempDoc of matchingTempDocs) {
        await tempDoc.incrementalPatch({
          showInSchedulerView: false,
          failedSync: false,
        });
      }
    } catch (error) {
      logError('Error handling operator state feed events', error, crumbleRxDocumentData(doc));
    }
  });

  // Failed to code states, show temp states with error, and original states in scheduler view
  if (borerOperatorChangeState)
    borerOperatorChangeState.error$.subscribe(async (error: RxError) => {
      logError(
        'Error during operator change replication',
        error,
        crumbleReplicationState(borerOperatorChangeState),
      );
      const hasExpiredToken = checkForExpiredToken(error);
      if (hasExpiredToken) return;

      const documentsData = error.parameters.pushRows?.[0]?.newDocumentState;
      const borerStateId = documentsData.borerStateId as string;
      const docId = documentsData.id as string;

      if (!borerStateId) {
        logError(
          'Failed to code state, and no borerStateId found in error. Cannot mark temp state as failedSync',
          error,
          crumbleReplicationState(borerOperatorChangeState),
        );
        return;
      }

      if (!recordIdHasFailedToSyncMoreThanXTimes(docId) && !isJestOrStorybook()) {
        // Allow Rxdb to retry (won't retry if failedSync is true)
        increaseFailedToSyncCount(docId);
        return;
      } else {
        // Toggle failedSync to true for temp items
        const tempStateDocsForBorerStateId = await borerOperatorChangeState.collection
          .find({
            selector: {
              borerStateId,
            },
          })
          .exec();

        tempStateDocsForBorerStateId.forEach(doc => doc.incrementalPatch({ failedSync: true }));

        delete recordIdsFailedToSyncCount[docId];

        // Unhide original delay in scheduler view (now with error)
        const syncStatesForBorerStateId = await stateFeedSyncState.collection
          .find({
            selector: {
              borerStateId,
            },
          })
          .exec();

        syncStatesForBorerStateId.forEach(doc =>
          doc.incrementalPatch({ showInSchedulerView: true }),
        );
      }
    });
};
